import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { TRANSLATIONS, LANGUAGES } from "@/i18n/translations";
import api from "@/lib/api";

const I18nContext = createContext(null);

// PawLink language policy (v7.42 — 2026-02-19)
// ------------------------------------------------------------
// English is the OFFICIAL default language across every PawLink
// surface (web, Android PWA, future iOS). We deliberately do NOT
// sniff `navigator.language`, geolocation, or OS locale — a first
// visit ALWAYS renders in English. The only way to switch is via
// the in-app language selector, which persists the choice in
// localStorage. If the stored value is missing, unknown, or
// corrupted we transparently fall back to English.
//
// The canonical policy is also served by the backend at
// `GET /api/i18n/policy` so that native apps (Android / iOS) can
// stay in lock-step without a client-side release. See
// /app/docs/LANGUAGE_POLICY.md for the full permanent standard.
const SUPPORTED_LANGS = ["en", "ar", "fr", "es"];
const DEFAULT_LANG = "en";
const STORAGE_KEY = "pawlink_lang";

function getInitialLang() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && SUPPORTED_LANGS.includes(stored) && TRANSLATIONS[stored]) {
            return stored;
        }
        // Corrupted / unknown value → clear it so we don't get stuck.
        if (stored) {
            try { localStorage.removeItem(STORAGE_KEY); } catch {}
        }
    } catch {}
    return DEFAULT_LANG;
}

function getInitialTheme() {
    try {
        const stored = localStorage.getItem("pawlink_theme");
        if (stored === "light" || stored === "dark" || stored === "system") return stored;
    } catch {}
    // PawLink v7.25 brand: dark is the default aesthetic
    return "dark";
}

function resolveTheme(t) {
    if (t === "system") {
        try {
            return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
        } catch { return "light"; }
    }
    return t === "dark" ? "dark" : "light";
}

function interpolate(str, vars) {
    if (!vars) return str;
    return String(str).replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
}

export function I18nProvider({ children }) {
    const [lang, setLang] = useState(getInitialLang);
    const [theme, setTheme] = useState(getInitialTheme);
    const [pickerNeeded, setPickerNeeded] = useState(false);

    useEffect(() => {
        const dir = TRANSLATIONS[lang]?.lang?.dir || "ltr";
        document.documentElement.setAttribute("lang", lang);
        document.documentElement.setAttribute("dir", dir);
        try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
    }, [lang]);

    useEffect(() => {
        const root = document.documentElement;
        const effective = resolveTheme(theme);
        if (effective === "dark") root.classList.add("dark"); else root.classList.remove("dark");
        try { localStorage.setItem("pawlink_theme", theme); } catch {}
        // Follow OS changes while `system` is selected
        if (theme !== "system") return;
        let mq;
        try { mq = window.matchMedia("(prefers-color-scheme: dark)"); } catch { return; }
        const handler = (e) => {
            if (e.matches) root.classList.add("dark"); else root.classList.remove("dark");
        };
        try { mq.addEventListener("change", handler); } catch { mq.addListener(handler); }
        return () => {
            try { mq.removeEventListener("change", handler); } catch { mq.removeListener(handler); }
        };
    }, [theme]);

    const t = useCallback((path, vars) => {
        const parts = path.split(".");
        let cur = TRANSLATIONS[lang];
        for (const p of parts) {
            if (cur && typeof cur === "object" && p in cur) cur = cur[p];
            else { cur = null; break; }
        }
        if (cur == null) {
            // fallback English
            let en = TRANSLATIONS.en;
            for (const p of parts) { if (en && p in en) en = en[p]; else { en = null; break; } }
            cur = en != null ? en : path;
        }
        if (typeof cur === "string") return interpolate(cur, vars);
        return cur;
    }, [lang]);

    const changeLang = useCallback(async (code) => {
        if (!TRANSLATIONS[code]) return;
        setLang(code);
        try { localStorage.setItem("pawlink_lang_chosen", "1"); } catch {}
        setPickerNeeded(false);
        try { await api.post("/me/preferences", { language: code }); } catch {}
    }, []);

    const toggleTheme = useCallback(() => setTheme((t) => (resolveTheme(t) === "dark" ? "light" : "dark")), []);
    const changeTheme = useCallback((next) => {
        setTheme(next === "dark" ? "dark" : next === "system" ? "system" : "light");
    }, []);

    const confirmPicker = useCallback((code) => {
        if (code) setLang(code);
        try { localStorage.setItem("pawlink_lang_chosen", "1"); } catch {}
        setPickerNeeded(false);
    }, []);

    return (
        <I18nContext.Provider value={{
            lang, setLang: changeLang, t, theme, toggleTheme, setTheme: changeTheme,
            languages: LANGUAGES, pickerNeeded, confirmPicker,
        }}>
            {children}
        </I18nContext.Provider>
    );
}

export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error("useI18n must be inside I18nProvider");
    return ctx;
}
